import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Ticket, TicketPriority, TicketStatus } from '@/types/tickets';
import { Clock, User, AlertCircle, Building2, CalendarDays } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import ResolutionTimeCountdown from './ResolutionTimeCountdown';
import { isAssignedToMe } from '@/lib/ticket-utils';

// Função auxiliar para formatar data de forma segura
const formatDateSafe = (dateString: string | undefined | null): string => {
  if (!dateString) return 'Data não disponível';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: ptBR
    });
  } catch {
    return 'Data inválida';
  }
};

interface TicketCardProps {
  ticket: Ticket;
  onClick: () => void;
}

const PRIORITY_COLORS: Record<TicketPriority, string> = {
  [TicketPriority.LOW]: 'bg-gray-100 text-gray-700',
  [TicketPriority.MEDIUM]: 'bg-blue-100 text-blue-700',
  [TicketPriority.HIGH]: 'bg-orange-100 text-orange-700',
  [TicketPriority.URGENT]: 'bg-red-100 text-red-700'
};

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  [TicketPriority.LOW]: 'Baixa',
  [TicketPriority.MEDIUM]: 'Média',
  [TicketPriority.HIGH]: 'Alta',
  [TicketPriority.URGENT]: 'Urgente'
};

const TicketCard: React.FC<TicketCardProps> = ({ ticket, onClick }) => {
  const { user: currentUser } = useAuth();
  
  // Validação de segurança
  if (!ticket || !ticket.id) {
    return null;
  }

  // Debug: Log para tickets em "Em Análise"
  if (ticket.stage?.name === 'Em Análise') {
    console.log(`[TicketCard] Ticket ${ticket.id} - Dados recebidos:`, {
      stage: ticket.stage?.name,
      resolution_time_hours: ticket.resolution_time_hours,
      resolution_time_set_at: ticket.resolution_time_set_at,
      ticket_keys: Object.keys(ticket),
      full_ticket: ticket
    });
  }

  // Verificar se o ticket está atribuído ao usuário atual (principal OU co-responsável)
  const isAssignedToCurrentUser = isAssignedToMe(ticket, currentUser?.id);
  
  // Verificar se está na etapa "Aberto"
  const isAbertoStage = ticket.stage?.name === 'Aberto';
  
  // is_viewed pode ser false, null ou undefined, todos devem ser tratados como "não visualizado"
  const isViewed = ticket.is_viewed === true;
  
  // Ticket não visualizado: atribuído ao usuário, não foi visualizado E está na etapa "Aberto"
  const isUnviewed = isAssignedToCurrentUser && !isViewed && isAbertoStage;
  
  // Ticket atribuído mas já visualizado: ainda deve ter destaque visual, mas mais sutil (apenas na etapa "Aberto")
  const isAssignedButViewed = isAssignedToCurrentUser && isViewed && isAbertoStage;
  
  // 🔍 LOG DETALHADO PARA DEBUG - SEMPRE executar para tickets atribuídos ao usuário
  if (isAssignedToCurrentUser) {
    console.log(`\n🔵 [TicketCard] Ticket ${ticket.id} - ANÁLISE DETALHADA:`, {
      title: ticket.title?.substring(0, 40),
      'assigned_to (ticket)': ticket.assigned_to,
      'currentUser?.id': currentUser?.id,
      'isAssignedToCurrentUser': isAssignedToCurrentUser,
      'stage': ticket.stage?.name,
      'isAbertoStage': isAbertoStage,
      'is_viewed (raw)': ticket.is_viewed,
      'is_viewed type': typeof ticket.is_viewed,
      'isViewed': isViewed,
      'isUnviewed': isUnviewed,
      'isAssignedButViewed': isAssignedButViewed,
      'VAI MOSTRAR DESTAQUE FORTE?': isUnviewed ? '✅ SIM (NOVO EM ABERTO)' : '❌ NÃO',
      'VAI MOSTRAR DESTAQUE SUTIL?': isAssignedButViewed ? '✅ SIM (ATRIBUÍDO EM ABERTO)' : '❌ NÃO'
    });
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: ticket.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  // 🔍 Log final antes de renderizar
  if (isAssignedToCurrentUser) {
    console.log(`🎨 [TicketCard] Renderizando Ticket ${ticket.id} - isUnviewed=${isUnviewed}, isAssignedButViewed=${isAssignedButViewed}`);
  }

  // Determinar classes CSS baseado no estado
  let cardClasses = 'cursor-pointer hover:shadow-md transition-shadow mb-3';
  let cardStyles: React.CSSProperties = { ...style };
  
  if (isUnviewed) {
    // Destaque forte para tickets não visualizados atribuídos ao usuário
    cardClasses += ' border-2 border-blue-500 border-l-4 border-l-blue-600 bg-blue-50/70 shadow-md ring-2 ring-blue-200';
    cardStyles = {
      ...cardStyles,
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: '#3b82f6',
      borderLeftWidth: '4px',
      borderLeftColor: '#2563eb',
      backgroundColor: 'rgba(239, 246, 255, 0.7)',
      boxShadow: '0 2px 4px 0 rgba(59, 130, 246, 0.2), 0 0 0 2px rgba(59, 130, 246, 0.1)'
    };
  } else if (isAssignedButViewed) {
    // Destaque sutil para tickets já visualizados mas atribuídos ao usuário
    cardClasses += ' border border-blue-300 border-l-2 border-l-blue-400 bg-blue-50/30';
    cardStyles = {
      ...cardStyles,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#93c5fd',
      borderLeftWidth: '2px',
      borderLeftColor: '#60a5fa',
      backgroundColor: 'rgba(239, 246, 255, 0.3)'
    };
  }

  return (
    <Card
      ref={setNodeRef}
      style={cardStyles}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={cardClasses}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold text-muted-foreground">#{ticket.id}</span>
            <h3 className="font-semibold text-sm line-clamp-2">
              {ticket.title}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {isUnviewed && (
              <Badge variant="default" className="bg-blue-600 text-white text-xs font-semibold animate-pulse shadow-sm">
                Novo
              </Badge>
            )}
            {isAssignedButViewed && (
              <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50 text-xs font-medium">
                Atribuído
              </Badge>
            )}
            <Badge className={`text-xs ${PRIORITY_COLORS[ticket.priority]}`}>
              {PRIORITY_LABELS[ticket.priority]}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 space-y-2">
        <p className="text-xs text-muted-foreground line-clamp-2">
          {ticket.description}
        </p>
        
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Building2 className="h-3 w-3" />
            <span className="truncate max-w-[120px]">
              {ticket.client?.legal_name || 'Cliente'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {ticket.reason && (
            <Badge variant="outline" className="text-xs">
              {ticket.reason.name}
            </Badge>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>
              {(() => {
                // Determinar qual data usar
                const isAberto = ticket.stage?.name?.toLowerCase().trim() === 'aberto';
                const dateToUse = isAberto 
                  ? ticket.created_at 
                  : (ticket.moved_to_stage_at || ticket.created_at);
                
                // Debug log para identificar problemas
                if (!dateToUse) {
                  console.warn(`[TicketCard] Ticket ${ticket.id} - Sem data disponível:`, {
                    stage: ticket.stage?.name,
                    isAberto,
                    created_at: ticket.created_at,
                    moved_to_stage_at: ticket.moved_to_stage_at,
                    ticket_keys: Object.keys(ticket)
                  });
                }
                
                return formatDateSafe(dateToUse);
              })()}
            </span>
          </div>
          
          {ticket.assignedUser && (
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span className="truncate max-w-[80px]">
                {ticket.assignedUser.name}
              </span>
              {ticket.coAssignees && ticket.coAssignees.length > 0 && (
                <span
                  className="text-xs text-muted-foreground"
                  title={ticket.coAssignees.map(a => a.name).join(', ')}
                >
                  +{ticket.coAssignees.length}
                </span>
              )}
            </div>
          )}
        </div>

        {ticket.due_date && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground pt-1">
            <CalendarDays className="h-3 w-3" />
            <span>
              Prazo: {format(new Date(ticket.due_date + 'T00:00:00'), 'dd/MM/yy', { locale: ptBR })}
            </span>
          </div>
        )}

        {/* Mostrar contagem regressiva se ticket está em "Em Análise" e tem previsão */}
        {(() => {
          const isEmAnalise = ticket.stage?.name === 'Em Análise';
          
          // Verificar se os campos existem e não são null/undefined
          const resolutionHours = ticket.resolution_time_hours ?? null;
          const resolutionSetAt = ticket.resolution_time_set_at ?? null;
          const hasResolutionTime = resolutionHours != null && resolutionSetAt != null;
          
          // Debug log detalhado
          if (isEmAnalise) {
            console.log(`[TicketCard] Ticket ${ticket.id} - Verificando contador:`, {
              isEmAnalise,
              resolution_time_hours: ticket.resolution_time_hours,
              resolution_time_set_at: ticket.resolution_time_set_at,
              resolutionHours,
              resolutionSetAt,
              hasResolutionTime,
              willShow: isEmAnalise && hasResolutionTime,
              stage: ticket.stage,
              allTicketKeys: Object.keys(ticket)
            });
          }
          
          if (isEmAnalise && hasResolutionTime) {
            return (
              <div className="pt-2 border-t mt-2">
                <ResolutionTimeCountdown
                  resolutionTimeHours={resolutionHours!}
                  resolutionTimeSetAt={resolutionSetAt!}
                  resolutionTimePausedAt={ticket.resolution_time_paused_at}
                  compact={true}
                />
              </div>
            );
          }
          
          return null;
        })()}
      </CardContent>
    </Card>
  );
};

export default TicketCard;

